import type * as models from '../models'

/**
 * Pagination values prepared for direct rendering.
 *
 * This view model intentionally contains no callbacks. The controlled config
 * owns page changes; this helper only normalizes values and creates display
 * metadata for the pagination template.
 */
export interface Pagination {
  readonly hasNext: boolean
  readonly hasPrevious: boolean
  readonly page: number
  readonly pageInputDisabled: boolean
  readonly pages: readonly (number | undefined)[]
  readonly rangeLabel: string
}

export namespace Pagination {
  /**
   * Normalizes controlled pagination without taking ownership of its state.
   *
   * Page and page-size values are clamped to positive integers. A source that
   * does not expose `totalCount` cannot produce numbered page buttons, so it
   * relies on its explicit `hasNext`/`hasPrevious` values instead.
   */
  export function get(config: models.Pagination | undefined): Pagination {
    if (!config) {
      return {
        hasNext: false,
        hasPrevious: false,
        page: 1,
        pageInputDisabled: false,
        pages: [],
        rangeLabel: '',
      }
    }

    const page = Math.max(Math.trunc(config.page), 1)
    const pageSize = Math.max(Math.trunc(config.pageSize), 1)
    // Numbered navigation and range labels are meaningful only for known
    // totals. Unknown-count APIs still render the direct next/previous controls
    // supplied by the adapter.
    const pageCount =
      config.totalCount === undefined
        ? undefined
        : Math.max(Math.ceil(config.totalCount / pageSize), 1)

    return {
      hasNext:
        config.hasNext ?? (pageCount === undefined ? false : page < pageCount),
      hasPrevious: config.hasPrevious ?? page > 1,
      page,
      pageInputDisabled: config.isPageInputDisabled ?? pageCount === 1,
      pages: pageCount === undefined ? [] : getPages(page, pageCount),
      rangeLabel: getRangeLabel(config.totalCount, pageSize, page),
    }
  }

  /**
   * Matches the legacy previous, current, and next page presentation.
   *
   * The first and last pages stay visible once the current page moves far
   * enough away from an edge; `undefined` entries represent the ellipsis gaps
   * consumed by the pagination template.
   */
  function getPages(
    currentPage: number,
    pageCount: number,
  ): readonly (number | undefined)[] {
    const previous =
      currentPage <= 4
        ? Array.from({ length: currentPage - 1 }, (_, index) => index + 1)
        : [1, undefined, currentPage - 2, currentPage - 1]
    const next =
      pageCount - currentPage <= 3
        ? Array.from(
            { length: Math.max(pageCount - currentPage, 0) },
            (_, index) => currentPage + index + 1,
          )
        : [currentPage + 1, currentPage + 2, undefined, pageCount]

    return [...previous, currentPage, ...next]
  }

  /**
   * Matches the legacy range text for known and unknown collection counts.
   */
  function getRangeLabel(
    totalCount: number | undefined,
    pageSize: number,
    page: number,
  ): string {
    if (totalCount === undefined || totalCount === 0) {
      return '1 - /'
    }

    const skip = (page - 1) * pageSize
    return `${skip + 1} - ${Math.min(skip + pageSize, totalCount)} / ${totalCount}`
  }
}
